import { Entity } from '@backstage/catalog-model';
import type { ErrorApi } from '@backstage/core-plugin-api';
import type { CatalogApi } from '@backstage/plugin-catalog-react';

export interface OwnedEntitiesQuery {
  catalogApi: CatalogApi;
  errorApi: ErrorApi;
  /** The caller's supervisory-org groups, as `useGetUserGroups` reports them. */
  userGroups: string[] | undefined;
  userGroupsLoading: boolean;
  userGroupsError: Error | undefined;
  /** Catalog filter to apply on top of `spec.owner`, e.g. kind and spec.type. */
  filter: Record<string, string>;
  fields: string[];
}

/**
 * Lists the entities matching `filter` that are owned by one of the caller's
 * own groups, one catalog query per group.
 *
 * Fails closed: while the group list is still resolving, when it could not be
 * fetched, and when the caller belongs to no group, the result is empty rather
 * than every entity in the catalog. A group-lookup failure is reported through
 * `errorApi` because the caller sees an empty list either way and would
 * otherwise have no idea why.
 */
export const listEntitiesOwnedByUserGroups = async ({
  catalogApi,
  errorApi,
  userGroups,
  userGroupsLoading,
  userGroupsError,
  filter,
  fields,
}: OwnedEntitiesQuery): Promise<Entity[]> => {
  if (userGroupsLoading) return [];

  if (userGroupsError) {
    errorApi.post(userGroupsError);
    return [];
  }

  if (!userGroups || userGroups.length === 0) {
    return [];
  }

  const responses = await Promise.all(
    userGroups.map(owner =>
      catalogApi.getEntities({
        filter: { ...filter, 'spec.owner': owner },
        fields,
      }),
    ),
  );

  return responses.flatMap(response => response.items);
};
